import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotBlank, _notNil } from '@/littledash';
import type { Animal, AnimalApiId } from '@/model/Animal.model';
import type { Sample } from '@/model/Sample.model';
import type { Study } from '@/model/Study.model';

export type BenchlingWorklist = components['schemas']['BenchlingWorklistV1'];
export type BenchlingWorklistSummary = components['schemas']['BenchlingWorklistSummaryV1'];
type SampleApiId = components['schemas']['SampleApiId'];

/**
 * Whether a selected record can actually be put in a worklist.
 *
 * The server enforces the synced half too - it is the same condition, checked there against the
 * database rather than the loaded rows. Checking here as well is purely so the action can be disabled
 * with an explanation instead of failing after the user commits to it.
 *
 * `api_id` is required as well as `benchling_entity_id` because the request is built from it; gating on
 * the entity id alone would let rows silently drop out of the payload.
 */
const isWorklistable = (record: { api_id?: string | null; benchling_entity_id?: string | null }): boolean =>
  _notNil(record.api_id) && _isNotBlank(record.benchling_entity_id ?? undefined);

/**
 * Benchling accepts at most this many entities per worklist call, mirrored by `maxItems` on the create
 * and add-items request schemas. Checked here so the action can be disabled up front rather than
 * rejected by the server.
 */
export const MAX_WORKLIST_ITEMS = 200;

export const exceedsWorklistLimit = (selectionCount: number): boolean => selectionCount > MAX_WORKLIST_ITEMS;

/**
 * Whether the study is set up in Benchling at all: synced, and pointed at the folder its records sync
 * into. Without both none of its records can be in Benchling, so the worklist action is hidden rather
 * than disabled - unlike an unsynced selection, it is not something the user can fix from the table.
 */
export const studyWorklistReady = (
  study: Pick<Study, 'synced_to_benchling' | 'benchling_integration_folder_id'> | undefined
): boolean => study?.synced_to_benchling === true && _isNotBlank(study.benchling_integration_folder_id);

export const allAnimalsSynced = (animals: Array<Animal>): boolean => animals.every(isWorklistable);

export const allSamplesSynced = (samples: Array<Sample>): boolean => samples.every(isWorklistable);

/** api_id is optional on the model, so narrow in one pass rather than mapping then filtering. */
export const animalApiIds = (animals: Array<Animal>): Array<AnimalApiId> =>
  animals.reduce<Array<AnimalApiId>>((ids, animal) => {
    if (_notNil(animal.api_id)) {
      // Animal types api_id as a plain string, so the prefix is asserted at this boundary.
      ids.push(animal.api_id as AnimalApiId);
    }
    return ids;
  }, []);

/** Sample rows come from the legacy API, so api_id is guarded at runtime despite its type. */
export const sampleApiIds = (samples: Array<Sample>): Array<SampleApiId> =>
  samples.reduce<Array<SampleApiId>>((ids, sample) => {
    if (_notNil(sample.api_id)) {
      ids.push(sample.api_id);
    }
    return ids;
  }, []);

const pluralise = (count: number, noun: string): string => `${count} ${count === 1 ? noun : `${noun}s`}`;

/** "3 animals", "1 sample", or "2 animals and 1 sample" - omitting whichever kind is absent. */
export const describeWorklistItems = (animalCount: number, sampleCount: number): string =>
  [
    ...(animalCount > 0 ? [pluralise(animalCount, 'animal')] : []),
    ...(sampleCount > 0 ? [pluralise(sampleCount, 'sample')] : []),
  ].join(' and ');
